import { Monitor, Smartphone, Tablet } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useFeedback, useUpdateFeedbackStatus, type FeedbackOut } from "@/api/hooks/feedback";
import { errorMessage } from "@/api/errors";
import { EmptyState } from "@/components/EmptyState";
import { ErrorNotice } from "@/components/ErrorNotice";
import { PageLoading } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { useOrgRole, useProjectParams } from "@/features/projects/projectContext";
import { cn } from "@/lib/utils";
import { filterByVersion, versionKeys, type VersionKey } from "./versions";
import { formatDateTime, formatRelative } from "@/lib/dates";

const DEVICE_ICONS = { desktop: Monitor, tablet: Tablet, phone: Smartphone } as const;
const STATUSES = ["new", "seen", "done"] as const;

function groupByPage(items: FeedbackOut[]): [string, FeedbackOut[]][] {
  const map = new Map<string, FeedbackOut[]>();
  for (const f of items) {
    const list = map.get(f.page) ?? [];
    list.push(f);
    map.set(f.page, list);
  }
  return [...map.entries()];
}

export function FeedbackPage() {
  const { t } = useTranslation();
  const { org, project } = useProjectParams();
  const isStaff = useOrgRole(org) === "staff";
  const feedback = useFeedback(org, project);
  const update = useUpdateFeedbackStatus(org, project);
  const [shot, setShot] = useState<FeedbackOut | null>(null);
  const [chosen, setChosen] = useState<VersionKey | null>(null);
  const chips = useMemo(() => versionKeys(feedback.data ?? []), [feedback.data]);
  const active = chosen !== null && chips.some((c) => c.key === chosen) ? chosen : null;
  const groups = useMemo(() => groupByPage(filterByVersion(feedback.data ?? [], active)), [feedback.data, active]);
  const chipLabel = (c: { key: VersionKey; batch?: number; version?: number }) =>
    c.batch !== undefined ? t("prototype.label", { batch: c.batch, version: c.version }) : t("feedback.versionUnknown");
  const itemLabel = (f: FeedbackOut) =>
    f.version_label ??
    (f.batch_number !== null && f.version !== null
      ? t("prototype.label", { batch: f.batch_number, version: f.version })
      : t("feedback.versionUnknown"));

  return (
    <section aria-labelledby="feedback-title" className="flex flex-col gap-6">
      <div>
        <h2 id="feedback-title">{t("feedback.title")}</h2>
        <p className="text-fg-muted">{t("feedback.intro")}</p>
      </div>
      {feedback.isPending ? <PageLoading /> : null}
      {feedback.isError ? <ErrorNotice error={feedback.error} /> : null}
      {feedback.data && feedback.data.length === 0 ? <EmptyState title={t("feedback.empty")} /> : null}
      {chips.length > 0 ? (
        <div role="group" aria-label={t("feedback.versionsShown")} className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-heading">{t("feedback.versionsShown")}</span>
          {[{ key: null, label: t("feedback.versionAll") }, ...chips.map((c) => ({ key: c.key, label: chipLabel(c) }))].map((c) => {
            const on = active === c.key;
            return (
              <button
                key={c.key ?? "all"}
                type="button"
                aria-pressed={on}
                onClick={() => setChosen(c.key)}
                className={cn(
                  "min-h-11 rounded-pill border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
                  on ? "border-secondary bg-secondary text-secondary-fg" : "border-border-strong bg-surface text-fg-muted hover:border-accent",
                )}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      ) : null}
      {groups.map(([page, items], gi) => (
        <section key={page} aria-labelledby={`fb-page-${gi}`} className="flex flex-col gap-3">
          <div className="flex items-baseline gap-3">
            <h3 id={`fb-page-${gi}`} className="font-mono text-lg">
              {page}
            </h3>
            <span className="text-sm text-fg-muted">{t("feedback.count", { count: items.length })}</span>
          </div>
          <Card>
            <ul className="divide-y divide-border">
              {items.map((f) => {
                const Icon = DEVICE_ICONS[f.device];
                const deviceLabel = t(`feedback.device.${f.device}`);
                return (
                  <li key={f.id} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start">
                    <span
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary-subtle text-heading"
                      title={`${deviceLabel} · ${t("feedback.viewport", f.viewport)}`}
                    >
                      <Icon role="img" aria-label={deviceLabel} className="h-5 w-5" />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <p className="whitespace-pre-wrap break-words">{f.comment}</p>
                      <p className="flex flex-wrap items-center gap-x-2 text-sm text-fg-muted">
                        <Badge variant="grey">{itemLabel(f)}</Badge>
                        <span>{f.author_name ?? t("feedback.anonymous")}</span>
                        {" · "}
                        <time dateTime={f.created_at} title={formatDateTime(f.created_at)}>
                          {formatRelative(f.created_at)}
                        </time>
                      </p>
                    </div>
                    <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                      {f.screenshot_url ? (
                        <button
                          type="button"
                          onClick={() => setShot(f)}
                          className="overflow-hidden rounded-md border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                          aria-label={t("feedback.viewScreenshot", { comment: f.comment })}
                        >
                          <img src={f.screenshot_url} alt="" className="h-16 w-24 object-cover" loading="lazy" />
                        </button>
                      ) : null}
                      {isStaff ? (
                        <Select
                          aria-label={t("feedback.status.label")}
                          value={f.status}
                          className="min-h-10 py-1 text-sm"
                          onChange={(e) =>
                            update.mutate(
                              { id: f.id, status: e.target.value as FeedbackOut["status"] },
                              {
                                onSuccess: () => toast.success(t("feedback.statusSaved")),
                                onError: (err) => toast.error(errorMessage(err, t("errors.generic"))),
                              },
                            )
                          }
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {t(`feedback.status.${s}`)}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Badge variant={f.status === "new" ? "default" : "grey"}>{t(`feedback.status.${f.status}`)}</Badge>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
        </section>
      ))}
      <Dialog open={shot !== null} onOpenChange={(o) => !o && setShot(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("feedback.screenshotTitle")}</DialogTitle>
            <DialogDescription>{shot?.comment}</DialogDescription>
          </DialogHeader>
          {shot?.screenshot_url ? (
            <img
              src={shot.screenshot_url}
              alt={t("feedback.screenshotAlt", { comment: shot.comment })}
              className="max-h-scroll w-full rounded-md object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
